// Chakra and React imports
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  Icon,
  SlideFade,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  useToast,
} from "@chakra-ui/react";
import Cookies from "universal-cookie";

// Components
import Card from "components/card/Card";
import { DetailedDataTable, HeatmapDataTable, COVERAGE_TABLE_VIRTUALIZE_THRESHOLD } from "./components/datatable";
import { OrderEditorModal } from "./components/orderEditorModal";
import AlertModal from "components/alertModal/AlertModal";
import { SelectButtonGroup } from "components/toggle/LabeledToggle";
import { ActionButton } from "./components/ActionButton";
import { useSimulationDeviationNotes } from "./hooks/useSimulationDeviationNotes";

// Icons
import { MdAddCircle, MdThumbUp } from "react-icons/md";
import { GiPartyPopper } from "react-icons/gi";
import { LuAlignVerticalSpaceAround } from "react-icons/lu";

// Api and Types
import {
  ApiLoadOrderList,
  ApiUpdateOrderList,
} from "api/coverage/buyOrderList";
import { ApiCoverageList } from "api/coverage/coverageList";
import { mapCoverageListFromApi } from "api/coverage/mapCoverageListResponse";
import {
  ApiProductsAttributes
} from "api/coverage/ProductAttributes";
import {
  ProductCoverage,
  BuyOrder,
  ProductProps,
} from "api/types";

// Loader
import { ThreeDots } from "react-loader-spinner";

// Context Providers
import { useLoading } from "contexts/LoadingContext";
import { useAuth } from "contexts/AuthProvider";
import { useSimulatorPremisesDrawer } from "contexts/SimulatorPremisesDrawerContext";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFinalizeSimulation } from "api/simulation/finalizeSimulation";
import { apiPutSimulationDeviations } from "api/simulation/saveSimulationDeviations";
import type { SimulationBootstrapPayload } from "api/simulation/createSimulation";
import {
  mapHttpErrorToSimulationLoadError,
} from "api/simulation/loadSavedSimulation";
import {
  fetchSimulationDeviationHydration,
  loadSavedSimulationSession,
} from "api/simulation/loadSimulationSession";
import type { SavedSimulationRow } from "api/simulation/simulationsList";
import { PremisesConfigurationContent } from "views/admin/simulatorPremises/PremisesConfigurationContent";
import { SavedSimulationsBoard } from "./components/SavedSimulationsBoard";

const simulationSessionCookie = new Cookies();
const ACTIVE_SIMULATION_STORAGE_KEY = "millennials-active-simulation-id";

function readSimulationSessionActive(): boolean {
  const v = simulationSessionCookie.get("millenials-user-simulating");
  return v === true || v === "true";
}

function readPersistedSimulationId(): string | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_SIMULATION_STORAGE_KEY);
    return raw?.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

function writePersistedSimulationId(id: string | null): void {
  try {
    if (id?.trim()) {
      sessionStorage.setItem(ACTIVE_SIMULATION_STORAGE_KEY, id.trim());
    } else {
      sessionStorage.removeItem(ACTIVE_SIMULATION_STORAGE_KEY);
    }
  } catch {
    // sessionStorage no disponible
  }
}

export default function LiveSimulator() {

  const [coverage, setCoverage] = useState<ProductCoverage[]>([]);
  const [buyOrders, setBuyOrders] = useState<BuyOrder[]>([]);
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [isReload, setReload] = useState(0);
  const [chronology, setChronology] = useState("year_month");
  const [viewMode, setViewMode] = useState("table");
  const [coverageSessionActive, setCoverageSessionActive] = useState(
    readSimulationSessionActive
  );
  const [tableIsLoading, setLoading] = useState(readSimulationSessionActive);
  const [userExit, setExit] = useState(false);
  const [error, setError] = useState(null);
  const [activeSimulationId, setActiveSimulationIdState] = useState<string | null>(
    () => (readSimulationSessionActive() ? readPersistedSimulationId() : null),
  );
  const setActiveSimulationId = useCallback((id: string | null) => {
    writePersistedSimulationId(id);
    setActiveSimulationIdState(id);
  }, []);
  const [loadingSavedSimulationId, setLoadingSavedSimulationId] = useState<
    string | null
  >(null);
  const [isFinalizingSimulation, setIsFinalizingSimulation] = useState(false);
  const routeBootstrapApplied = useRef(false);
  const deviationGeneralHydratedForSim = useRef<string | null>(null);
  const sessionCoverageCacheRef = useRef<{
    simulationId: string;
    chronology: string;
  } | null>(null);
  const [screenH, setScreenH] = useState(window.screen.availHeight)
  window.addEventListener('resize', (...args) => {setScreenH(window.screen.availHeight)})

  const { isOpen, onOpen, onClose } = useDisclosure();
  const handleFinalizeDialogClose = useCallback(() => {
    if (!isFinalizingSimulation) {
      onClose();
    }
  }, [isFinalizingSimulation, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setIsFinalizingSimulation(false);
    }
  }, [isOpen]);
  const {
    isOpen: modalIsOpen,
    onOpen: modalOnOpen,
    onClose: modalOnClose,
  } = useDisclosure();

  const { show, hide, isLoaded } = useLoading();
  const { userActions, logoutAction, user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const premisesDrawer = useSimulatorPremisesDrawer();
  const toast = useToast();

  useLayoutEffect(() => {
    const onListing = !coverageSessionActive;
    premisesDrawer.setListingMode(onListing);
    if (!onListing && premisesDrawer.isOpen) {
      premisesDrawer.onClose();
    }
  }, [
    coverageSessionActive,
    premisesDrawer.isOpen,
    premisesDrawer.onClose,
    premisesDrawer.setListingMode,
  ]);

  const {
    setProductGeneralReason,
    getPeriodDeviationNoteId,
    getProductGeneralNoteId,
    getProductGeneralNoteLabel,
    getPeriodDeviationNoteLabel,
    setPeriodDeviation,
    hydrateDeviationNotesState,
    resetDeviationNotesState,
    getPayloadForSimulation,
  } = useSimulationDeviationNotes();

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const drawerSubtitleColor = useColorModeValue("secondaryGray.600", "gray.400");
  const loaderWrapperBg = useColorModeValue("#ffffff", "#111c44");
  const drawerSurfaceBg = useColorModeValue("secondaryGray.100", "navy.900");

  const syncDeviationsForSimulation = useCallback(
    async (simId: string, chronologyParam: string, rows: ProductCoverage[]) => {
      try {
        const hydration = await fetchSimulationDeviationHydration(
          simId,
          chronologyParam,
          rows,
        );
        hydrateDeviationNotesState(hydration);
      } catch {
        resetDeviationNotesState();
      }
    },
    [hydrateDeviationNotesState, resetDeviationNotesState],
  );

  const getCoverage = async (
    chronologyParam: string,
    simulationId: string | null,
  ) => {
    show();
    setLoading(true);
    await ApiCoverageList(chronologyParam, simulationId)
      .then(async (response) => {
        const mapped = mapCoverageListFromApi(response.data);
        setCoverage(mapped);
        if (simulationId) {
          await syncDeviationsForSimulation(
            simulationId,
            chronologyParam,
            mapped,
          );
        } else {
          resetDeviationNotesState();
        }
      })
      .catch((err) => {
        const response = err.response;
        if (response?.status === 403) {
          logoutAction(true);
          return;
        }
        const mapped = mapHttpErrorToSimulationLoadError(err);
        setError({
          type: "error",
          message: mapped.message,
          title: mapped.userTitle,
        });
      })
      .finally(() => {
        setLoading(false);
        hide();
      });
  };

  const handleDeviationReasonSaved = useCallback(
    (productId: number, deviationNoteId: string, label: string) => {
      setProductGeneralReason(productId, deviationNoteId, label);
      setCoverage((prev) =>
        prev.map((row) =>
          row.productProps.id === productId
            ? {
                ...row,
                productProps: {
                  ...row.productProps,
                  coverageDeviationReason: label,
                },
              }
            : row,
        ),
      );
    },
    [setProductGeneralReason],
  );

  const handlePeriodDeviationChange = useCallback(
    (input: Parameters<typeof setPeriodDeviation>[0]) => {
      setPeriodDeviation(input);
    },
    [setPeriodDeviation],
  );

  const canEditDeviationReason = useMemo(() => {
    if (!user) {
      return false;
    }
    if (user.is_staff) {
      return true;
    }
    return user.checkPermission(
      "Coverage simulator",
      "change_coverageparameter",
    );
  }, [user]);

  /** Misma regla que motivo general; Agente 8 puede ajustar permiso si aplica otro codename. */
  const canEditPeriodDeviation = canEditDeviationReason;

  const handleViewSwitchToggle = (value: string) => {
    setLoading(true);
    setViewMode(value);
  };

  const getBuyOrders = async () => {
    show();
    await ApiLoadOrderList(true).then((response) => {
      let data: BuyOrder[] = response.data;
      setBuyOrders(data);
    }).catch((err) => { if (err.response?.status === 403) { logoutAction(true) } });
    hide();
  };

  const getProducts = async () => {
    await ApiProductsAttributes().then((response) => {
      let data: ProductProps[] = response.data;
      setProducts(data);
    }).catch((err) => { if (err.response?.status === 403) { logoutAction(true) } });
  };

  const resetOrderList = async () => {
    await ApiUpdateOrderList([]).then().catch((err) => { if (err.response?.status === 403) { logoutAction(true) } });
  };

  useEffect(() => {
    getProducts();
  }, []);

  const applySessionBootstrap = useCallback(
    (payload: SimulationBootstrapPayload) => {
      setActiveSimulationId(payload.simulationId);
    },
    [setActiveSimulationId],
  );

  const handleLoadSavedSimulation = useCallback(
    async (row: SavedSimulationRow) => {
      setLoadingSavedSimulationId(row.id);
      setError(null);
      setCoverage([]);
      sessionCoverageCacheRef.current = null;
      try {
        const session = await loadSavedSimulationSession(row.id, chronology);
        resetDeviationNotesState();
        deviationGeneralHydratedForSim.current = null;
        applySessionBootstrap(session.bootstrap);
        if (session.chronology && session.chronology !== chronology) {
          setChronology(session.chronology);
        }
        sessionCoverageCacheRef.current = {
          simulationId: session.bootstrap.simulationId,
          chronology: session.chronology,
        };
        setCoverageSessionActive(true);
        userActions.simulating.change(true);

        if (session.coverage && session.coverage.length > 0) {
          setCoverage(session.coverage);
          setLoading(false);
          hide();
        }

        hydrateDeviationNotesState(
          session.deviationHydration ?? {
            byProductGeneral: {},
            byProductPeriod: {},
          },
        );
        if (session.deviationHydration?.byProductGeneral) {
          deviationGeneralHydratedForSim.current =
            session.bootstrap.simulationId;
        }
        setReload((r) => r + 1);

        const displayName =
          session.bootstrap.simulationName?.trim() ||
          row.simulation_name?.trim() ||
          `Simulación #${row.id}`;
        toast({
          title: "Simulación abierta",
          description: session.coverageFromSnapshot
            ? `Resultados guardados de «${displayName}» cargados desde el servidor.`
            : `Abriendo «${displayName}» (sin snapshot; se recalculará la cobertura).`,
          status: session.coverageFromSnapshot ? "success" : "info",
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        const mapped = mapHttpErrorToSimulationLoadError(err);
        if (mapped.statusCode === 403) {
          logoutAction(true);
          return;
        }
        toast({
          title: mapped.userTitle,
          description: mapped.message,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setLoadingSavedSimulationId(null);
      }
    },
    [
      applySessionBootstrap,
      chronology,
      hide,
      hydrateDeviationNotesState,
      logoutAction,
      resetDeviationNotesState,
      toast,
      userActions,
    ],
  );

  useEffect(() => {
    const st = (
      location.state as { simulationBootstrap?: SimulationBootstrapPayload } | null
    )?.simulationBootstrap;
    if (!st) {
      routeBootstrapApplied.current = false;
      return;
    }
    if (routeBootstrapApplied.current) {
      return;
    }
    routeBootstrapApplied.current = true;
    resetDeviationNotesState();
    deviationGeneralHydratedForSim.current = null;
    sessionCoverageCacheRef.current = null;
    applySessionBootstrap(st);
    setCoverageSessionActive(true);
    userActions.simulating.change(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [
    location.state,
    location.pathname,
    location.search,
    applySessionBootstrap,
    nav,
    resetDeviationNotesState,
    userActions,
  ]);

  useEffect(() => {
    if (!coverageSessionActive) {
      setLoading(false);
      return;
    }
    getBuyOrders();
  }, [isReload, coverageSessionActive]);

  useEffect(() => {
    if (!coverageSessionActive) {
      return;
    }
    const cached = sessionCoverageCacheRef.current;
    if (
      cached &&
      cached.simulationId === activeSimulationId &&
      cached.chronology === chronology
    ) {
      return;
    }
    getCoverage(chronology, activeSimulationId);
  }, [isReload, chronology, coverageSessionActive, activeSimulationId]);

  const MemoizedDetailedDataTable = useMemo(() => {
    return coverage.length !== 0 ? (
      <DetailedDataTable
        data={coverage}
        chronologyParameter={chronology}
        key={`${activeSimulationId ?? "none"}-${coverage.length}-${isReload}`}
        virtualize={coverage.length >= COVERAGE_TABLE_VIRTUALIZE_THRESHOLD}
        setLoading={setLoading}
        canEditDeviationReason={canEditDeviationReason}
        getProductGeneralNoteId={getProductGeneralNoteId}
        getProductGeneralNoteLabel={getProductGeneralNoteLabel}
        onDeviationReasonSaved={handleDeviationReasonSaved}
        canEditPeriodDeviation={canEditPeriodDeviation}
        getPeriodDeviationNoteId={getPeriodDeviationNoteId}
        getPeriodDeviationNoteLabel={getPeriodDeviationNoteLabel}
        onPeriodDeviationChange={handlePeriodDeviationChange}
      />
    ) : null;
  }, [
    activeSimulationId,
    coverage,
    chronology,
    canEditDeviationReason,
    canEditPeriodDeviation,
    handleDeviationReasonSaved,
    getProductGeneralNoteId,
    getProductGeneralNoteLabel,
    getPeriodDeviationNoteId,
    getPeriodDeviationNoteLabel,
    handlePeriodDeviationChange,
    isReload,
  ]);

  const MemoizedHeatmapDataTable = useMemo(() => {
    return coverage.length !== 0 ? (
      <HeatmapDataTable
        data={coverage}
        chronologyParameter={chronology}
        key={coverage.length}
        virtualize={coverage.length >= COVERAGE_TABLE_VIRTUALIZE_THRESHOLD}
        setLoading={setLoading}
      />
    ) : null;
  }, [coverage, chronology]);

  const controlsLocked = !coverageSessionActive || tableIsLoading;

  return (
    <Box pt={{ base: "100px", md: "70px" }}>
      <Drawer
        isOpen={premisesDrawer.isOpen}
        placement="left"
        onClose={premisesDrawer.onClose}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent
          maxW={{ base: "100%", md: "520px" }}
          w={{ base: "100%", md: "520px" }}
          maxH="100vh"
          display="flex"
          flexDirection="column"
          bg={drawerSurfaceBg}
        >
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" flexShrink={0} pe={12}>
            <Text fontSize="lg" fontWeight="700" lineHeight="short">
              Premisas de cobertura
            </Text>
            <Text
              fontSize="sm"
              fontWeight="normal"
              color={drawerSubtitleColor}
              mt={1}
            >
              Fuentes ERP, integraciones y cargas antes de simular
            </Text>
          </DrawerHeader>
          <DrawerBody
            display="flex"
            flexDirection="column"
            flex="1"
            minH={0}
            overflowY="auto"
            pb={6}
            px={4}
          >
            <PremisesConfigurationContent
              variant="drawer"
              existingSimulationId={activeSimulationId}
              onReadyToSimulate={(payload) => {
                resetDeviationNotesState();
                deviationGeneralHydratedForSim.current = null;
                sessionCoverageCacheRef.current = null;
                setCoverage([]);
                setActiveSimulationId(
                  payload?.simulationId ?? activeSimulationId,
                );
                setCoverageSessionActive(true);
                setReload((r) => r + 1);
                premisesDrawer.onClose();
              }}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {error != null ? (
        <AlertModal
          status={error.type}
          message={error.message}
          title={error.title}
          disclosure={{ isOpen: error != null, onClose: onClose }}
          iconProps={{ w: "70px", h: "75px" }}
          afterCloseAction={() => {
            userActions.simulating.change(false);
            setCoverageSessionActive(false);
            setActiveSimulationId(null);
            deviationGeneralHydratedForSim.current = null;
            resetDeviationNotesState();
            nav('/coverage/simulator');
          }}
        />
      ) : null}
      <AlertModal
        status="warning"
        message={
          <Box mb="-5px">
            {userExit ? (
              <SlideFade
                in={userExit}
                delay={0.2}
                offsetY="50px"
                style={{ marginBottom: "-5px" }}
              >
                <Text color={textColorPrimary} fontWeight="500">
                  ¡Éxito!
                </Text>
                <Icon
                  as={GiPartyPopper}
                  w="100px"
                  h="100px"
                  mt="20px"
                  color={textColorPrimary}
                />
              </SlideFade>
            ) : null}
            {!userExit ? (
              <SlideFade
                in={isOpen && !userExit}
                delay={0.1}
                offsetY="50px"
                style={{ marginBottom: "-5px" }}
              >
                <Text mt="5px">
                  ¿Está seguro de que desea finalizar esta simulación?
                </Text>
                <Icon
                  as={MdThumbUp}
                  w="300px"
                  h="100px"
                  mt="20px"
                  color={textColorPrimary}
                />
              </SlideFade>
            ) : null}
          </Box>
        }
        title="¿Todo listo?"
        titleProps={{ textAlign: "center" }}
        messageProps={{ textAlign: "center", width: "100%" }}
        disclosure={{ isOpen: isOpen, onOpen: onOpen, onClose: handleFinalizeDialogClose }}
        iconProps={{ display: "none" }}
        buttons={
          <>
            <Button
              onClick={handleFinalizeDialogClose}
              isDisabled={isFinalizingSimulation}
            >
              No
            </Button>
            <Button
              ml="15px"
              colorScheme="green"
              isLoading={isFinalizingSimulation}
              loadingText="Finalizando..."
              isDisabled={isFinalizingSimulation}
              onClick={async () => {
                setIsFinalizingSimulation(true);
                try {
                  if (!activeSimulationId) {
                    toast({
                      title: "No hay simulación activa",
                      description:
                        "Abrí o creá una simulación antes de finalizar.",
                      status: "error",
                      duration: 6000,
                      isClosable: true,
                    });
                    setIsFinalizingSimulation(false);
                    return;
                  }
                  const erpCodeByProductId = new Map(
                    coverage.map((row) => [
                      row.productProps.id,
                      row.productProps.erpCode,
                    ]),
                  );
                  const deviationPayload = getPayloadForSimulation(
                    activeSimulationId,
                    erpCodeByProductId,
                  );
                  if (deviationPayload.length > 0) {
                    await apiPutSimulationDeviations(
                      activeSimulationId,
                      deviationPayload,
                    );
                  }
                  await apiFinalizeSimulation(activeSimulationId);
                  // Persiste snapshot de cobertura para reaperturas futuras.
                  try {
                    await ApiCoverageList(chronology, activeSimulationId);
                  } catch (snapshotError) {
                    console.error(snapshotError);
                    toast({
                      title: "Simulación finalizada",
                      description:
                        "No se pudo guardar el snapshot de cobertura; podés reabrirla desde el listado.",
                      status: "warning",
                      duration: 8000,
                      isClosable: true,
                    });
                  }
                } catch (e) {
                  console.error(e);
                  toast({
                    title: "No se pudo finalizar",
                    description:
                      "Guardá los motivos por período e intentá de nuevo.",
                    status: "error",
                    duration: 6000,
                    isClosable: true,
                  });
                  setIsFinalizingSimulation(false);
                  return;
                }
                userActions.simulating.change(false);
                setCoverageSessionActive(false);
                setActiveSimulationId(null);
                deviationGeneralHydratedForSim.current = null;
                resetDeviationNotesState();
                setExit(true);
                onClose();
                await new Promise((r) => setTimeout(r, 1000));
                resetOrderList();
                nav("/coverage/simulator");
              }}
            >
              Si
            </Button>
          </>
        }
      />
      <OrderEditorModal
        orders={buyOrders}
        products={products}
        simulationId={activeSimulationId}
        reloader={setReload}
        modalDisclosure={{ modalIsOpen, modalOnClose }}
      />
      {!coverageSessionActive ? (
        <SlideFade in={true} delay={0.2} offsetX="50px" offsetY="0px">
          <SavedSimulationsBoard
            fetchFromApi={true}
            loadingSimulationId={loadingSavedSimulationId}
            onSelectSimulation={handleLoadSavedSimulation}
          />
        </SlideFade>
      ) : null}
      {!coverageSessionActive ? (
        <SlideFade in={true} delay={0.35} offsetX="50px" offsetY="0px">
          <Card p={8} mb={4}>
            <Text textAlign="center" color={textColorPrimary} fontWeight="600">
              Configurá las premisas y cargá la simulación
            </Text>
            <Text textAlign="center" fontSize="sm" mt={4} color={textColorPrimary}>
              Abrí las premisas con el ícono de filtros en la barra superior (junto a
              tu perfil), ajustá los parámetros y pulsá{" "}
              <Text as="span" fontWeight="700">
                Cargar
              </Text>{" "}
              para crear la simulación en el servidor (POST /api/v1/simulations/) y ver
              los resultados en el mapa.
            </Text>
          </Card>
        </SlideFade>
      ) : null}
      {coverageSessionActive ? (
      <SlideFade in={isLoaded} delay={0.5} offsetX="50px" offsetY="0px">
        <Card p={2}>
          <Flex
            w="100%"
            justifyContent={{ md: "space-between", base: "center" }}
            direction={{ base: "column", md: "row" }}
          >
            <Flex
              w={{ base: "auto", lg: "30%" }}
              alignItems="center"
              justifyContent="center"
            >
              <SelectButtonGroup onChange={handleViewSwitchToggle} value={viewMode} groupProps={{ isAttached: true, variant: 'brand', size: 'sm', w: {base: '100%', md: '45%'}, display: {base: 'none', md: 'block'}, isDisabled: controlsLocked }}>
                <Button value={'table'} w="50%" fontSize={{ base: '12px', md: '12px', lg: "sm" }} isDisabled={controlsLocked}>Detalle</Button>
                <Button value={'heatmap'} w="50%" fontSize={{ base: '12px', md: '12px', lg: "sm" }} isDisabled={controlsLocked}>Simple</Button>
              </SelectButtonGroup>
            </Flex>
            <Flex justifyContent='space-between' flexDirection={{ base: "column", md: "row" }} w={{ base: 'auto', md: "63%", lg: "69%" }}>
              <ActionButton onClick={modalOnOpen} icon={MdAddCircle} text="Simular Llegadas" buttonProps={{display: {base: 'none', lg: 'block'}, isDisabled: controlsLocked }}/>
              <ActionButton onClick={onOpen} icon={MdThumbUp} text="Finalizar Simulación" buttonProps={{isDisabled: controlsLocked}}/>
            </Flex>
          </Flex>
        </Card>
      </SlideFade>
      ) : null}
      <SlideFade in={isLoaded} delay={0.5} offsetX="50px" offsetY="0px">
        <Box textAlign="center" height="325px" alignContent="center" display={{ base: coverageSessionActive ? "block" : "none", md: "none" }}>
          <Text >
            Voltee su dispositivo para visualizar la tabla.
          </Text>
          <Icon as={LuAlignVerticalSpaceAround} mt="20px" w="100px" h="100px" />
        </Box>
      </SlideFade>
      <SlideFade in={isLoaded} delay={0.75} offsetX="0px" offsetY="50px">
          <Card
            flexDirection="column"
            w="100%"
            mt="1rem"
            pt="0px"
            pl="15px"
            pr="25px"
            pb="20px"
            h={{ lg: screenH-180, "2xl": screenH-265 }}
            overflow="hidden"
            display={{ base: "none", md: coverageSessionActive ? "flex" : "none" }}
          >
            <ThreeDots
              visible={coverageSessionActive && tableIsLoading}
              height="76vh"
              width="70"
              wrapperStyle={{
                width: "100%",
                heigth: "100%",
                justifyContent: "center",
                backgroundColor: `${loaderWrapperBg}`,
                zIndex: "100",
              }}
              color="#7551ff"
            />
            <Box flex="1" minH={0} minW={0} w="100%">
              {viewMode==='table'?MemoizedDetailedDataTable:MemoizedHeatmapDataTable}
            </Box>
          </Card>
      </SlideFade>
    </Box>
  );
}
